import JSZip from 'jszip';

const VIEW_LABELS = ['avant', 'droite', 'arriere', 'gauche'] as const;

const ZONE_LABELS: Record<string, string> = {
  front: 'Avant',
  back: 'Arriere',
  leftSleeve: 'Manche gauche',
  rightSleeve: 'Manche droite',
};

export type MaterialInfo = { id: string; name: string; group: string; element: string };
export type AssetRefCollection = 'design' | 'motif';
export type ResolvedAssetRef = {
  name?: string;
  url?: string;
  dataUrl?: string;
};

function looksLikeDataUrl(value: string): boolean {
  return /^data:/i.test(String(value || '').trim());
}

function dataUrlToBytes(dataUrl: string): Uint8Array {
  const base64 = dataUrl.includes(',') ? dataUrl.split(',')[1] : dataUrl;
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

function parseDataUrlPayload(dataUrl: string): { bytes: Uint8Array; mime: string } | null {
  const raw = String(dataUrl || '').trim();
  const commaIndex = raw.indexOf(',');
  if (commaIndex <= 0) return null;
  const header = raw.slice(0, commaIndex);
  const payload = raw.slice(commaIndex + 1);
  if (!payload) return null;
  const mimeMatch = header.match(/^data:([^;]+)/i);
  const mime = (mimeMatch?.[1] || 'application/octet-stream').toLowerCase();
  const isBase64 = /;base64/i.test(header);

  if (isBase64) {
    try {
      const normalized = payload.replace(/\s+/g, '');
      const binary = atob(normalized);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
      return { bytes, mime };
    } catch {
      return null;
    }
  }

  try {
    const decoded = decodeURIComponent(payload);
    const bytes = new TextEncoder().encode(decoded);
    return { bytes, mime };
  } catch {
    try {
      const bytes = new TextEncoder().encode(payload);
      return { bytes, mime };
    } catch {
      return null;
    }
  }
}

function inferExtensionFromMime(mime: string): string {
  const normalized = (mime || '').toLowerCase();
  if (normalized.includes('svg')) return 'svg';
  if (normalized.includes('png')) return 'png';
  if (normalized.includes('jpeg') || normalized.includes('jpg')) return 'jpg';
  if (normalized.includes('webp')) return 'webp';
  if (normalized.includes('gif')) return 'gif';
  if (normalized.includes('bmp')) return 'bmp';
  if (normalized.includes('tiff')) return 'tiff';
  if (normalized.includes('json')) return 'json';
  if (normalized.includes('pdf')) return 'pdf';
  return 'bin';
}

function inferExtensionFromPath(value: string): string | null {
  const raw = String(value || '').trim();
  if (!raw) return null;
  const cleaned = raw.split('#')[0].split('?')[0];
  const tail = cleaned.split('/').pop() || '';
  const match = tail.match(/\.([a-z0-9]{1,8})$/i);
  if (!match) return null;
  return match[1].toLowerCase();
}

function sanitizeFileStem(value: string): string {
  const stem = String(value || '')
    .replace(/\.[^/.]+$/, '')
    .replace(/[\\/:*?"<>|]+/g, ' ')
    .replace(/[^a-zA-Z0-9 _.-]+/g, ' ')
    .replace(/[\u0000-\u001f]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
  const limited = stem.slice(0, 80).trim();
  return limited || 'asset';
}

function ensureUniqueFileName(baseName: string, ext: string, used: Set<string>): string {
  const safeExt = (ext || 'bin').replace(/^\.+/, '').toLowerCase() || 'bin';
  const safeBase = sanitizeFileStem(baseName);
  let candidate = `${safeBase}.${safeExt}`;
  let index = 2;
  while (used.has(candidate.toLowerCase())) {
    candidate = `${safeBase}_${index}.${safeExt}`;
    index += 1;
  }
  used.add(candidate.toLowerCase());
  return candidate;
}

async function fetchAssetBytesFromUrl(url: string): Promise<{ bytes: Uint8Array; ext: string } | null> {
  try {
    const response = await fetch(url);
    if (!response.ok) return null;
    const buffer = await response.arrayBuffer();
    const bytes = new Uint8Array(buffer);
    if (bytes.length === 0) return null;
    const mime = response.headers.get('content-type') || '';
    const extFromMime = inferExtensionFromMime(mime);
    const extFromPath = inferExtensionFromPath(url);
    return { bytes, ext: extFromPath || extFromMime || 'bin' };
  } catch {
    return null;
  }
}

type UsedAssetRef = {
  collection?: AssetRefCollection;
  value: string;
  label: string;
};

function collectUsedAssetRefs(layers: any[]): UsedAssetRef[] {
  const refs: UsedAssetRef[] = [];
  const seen = new Set<string>();

  for (const layer of layers) {
    const sourceIsAssetRef = layer?.source?.kind === 'assetRef';
    const sourceValue = sourceIsAssetRef ? layer?.source?.value : undefined;
    const fallbackMotifSourceKey = layer?.data?.isMotif ? layer?.data?.motifSourceKey : undefined;
    const value = String(sourceValue || fallbackMotifSourceKey || '').trim();
    if (!value) continue;

    const explicitCollection =
      layer?.source?.collection === 'motif' || layer?.source?.collection === 'design'
        ? (layer.source.collection as AssetRefCollection)
        : undefined;
    const valueLooksMotif = /(^|\/)motif(s)?(\/|$)/i.test(value);
    const inferredCollection =
      explicitCollection ||
      (layer?.data?.isPatternMotif ? 'motif' : valueLooksMotif ? 'motif' : 'design');
    const key = `${inferredCollection || 'any'}::${value}`;
    if (seen.has(key)) continue;
    seen.add(key);

    refs.push({
      collection: inferredCollection,
      value,
      label: String(layer?.data?.motifLabel || layer?.data?.fileName || value),
    });
  }

  return refs;
}

function radToDeg(rad: number): number {
  return Math.round((rad * 180) / Math.PI);
}

const gradientValueRegex =
  /^linear-gradient\(([-+]?\d+(?:\.\d+)?)deg,\s*(#[0-9a-fA-F]{6}),\s*(#[0-9a-fA-F]{6})(?:,\s*(\d{1,3}(?:\.\d+)?)%)?\)$/;

function parseGradientValue(value: string): { angle: number; start: string; end: string; stop: number } | null {
  const raw = String(value || '').trim();
  const match = raw.match(gradientValueRegex);
  if (!match) return null;
  const angle = Number(match[1]);
  const stopRaw = Number(match[4] ?? 50);
  const stop = Math.min(100, Math.max(0, Number.isFinite(stopRaw) ? stopRaw : 50));
  return {
    angle: Number.isFinite(angle) ? angle : 90,
    start: match[2],
    end: match[3],
    stop,
  };
}

function getPatternDesignLabel(patternDesignKey: string): string {
  const normalized = String(patternDesignKey || '').trim().toLowerCase();
  // Internal keys are inverted versus the UI target selection.
  if (normalized === 'design1') return 'Design 2';
  if (normalized === 'design2') return 'Design 1';
  return patternDesignKey || '-';
}

function appendRecapColor(
  lines: string[],
  label: string,
  value: string | undefined,
  indent = '  '
): void {
  const raw = String(value ?? '-');
  const gradient = parseGradientValue(raw);
  const safeLabel = label.trim().endsWith(':') ? label.trim() : `${label.trim()}:`;
  if (!gradient) {
    lines.push(`${indent}${safeLabel.padEnd(18)} ${raw}`);
    return;
  }
  lines.push(`${indent}${safeLabel.padEnd(18)} DEGRADE`);
  lines.push(`${indent}  Couleur 1 : ${gradient.start}`);
  lines.push(`${indent}  Couleur 2 : ${gradient.end}`);
  lines.push(`${indent}  Rotation  : ${Math.round(gradient.angle)} deg`);
  lines.push(`${indent}  Translate : ${(gradient.stop / 100).toFixed(2)} (${Math.round(gradient.stop)}%)`);
}

function buildRecap(creation: any, materials: MaterialInfo[], modelName: string): string {
  const lines: string[] = [];
  const date = new Date().toLocaleDateString('fr-FR', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

  lines.push('=== RECAPITULATIF DE CREATION INDYANNA ===');
  lines.push(`Date        : ${date}`);
  lines.push(`Modele      : ${modelName}`);
  lines.push('');

  const { materialColors = {}, patternMotifColors } = creation.state ?? {};
  const layers: any[] = (creation.state?.layers ?? [])
    .filter((l: any) => !l.data?.isSystem)
    .sort((a: any, b: any) => (a.order ?? 0) - (b.order ?? 0));

  const colorEntries = Object.entries(materialColors as Record<string, string>);
  if (colorEntries.length) {
    lines.push('--- COULEURS DU PRODUIT ---');
    for (const [matId, colorValue] of colorEntries) {
      const mat = materials.find(m => m.id === matId);
      const label = mat ? `${mat.group} - ${mat.element}` : matId;
      appendRecapColor(lines, label, String(colorValue || '-'));
    }
  }

  const baseMotifLayers = layers.filter((layer: any) => layer?.data?.isMotif && !layer?.data?.isPatternMotif);
  const uniqueBaseMotifLayers: any[] = [];
  const seenBaseMotifIds = new Set<string>();
  for (const layer of baseMotifLayers) {
    const motifId = String(layer?.data?.motifId || layer?.id || '');
    if (motifId && seenBaseMotifIds.has(motifId)) continue;
    if (motifId) seenBaseMotifIds.add(motifId);
    uniqueBaseMotifLayers.push(layer);
  }

  if (uniqueBaseMotifLayers.length > 0) {
    lines.push('');
    lines.push('--- COULEURS DU DESIGN ---');
    for (const motifLayer of uniqueBaseMotifLayers) {
      const motifName =
        motifLayer?.data?.motifLabel ||
        motifLayer?.data?.fileName ||
        motifLayer?.data?.motifSourceKey ||
        'Inconnu';
      const motifDesignColors =
        (motifLayer?.data?.motifDesignColors as { design1?: string; design2?: string } | undefined) || {};
      lines.push(`[MOTIF] ${motifName}`);
      appendRecapColor(lines, 'Design 1', motifDesignColors.design1 ?? '-');
      appendRecapColor(lines, 'Design 2', motifDesignColors.design2 ?? '-');
      lines.push('');
    }
    while (lines.length > 0 && lines[lines.length - 1] === '') {
      lines.pop();
    }
  }

  const hasPatternMotifLayers = layers.some((layer: any) => layer?.data?.isMotif && layer?.data?.isPatternMotif);
  if (hasPatternMotifLayers) {
    lines.push('');
    lines.push('--- COULEURS MOTIF (SUPERPOSITION) ---');
    appendRecapColor(lines, 'Motif 1', patternMotifColors?.motif1 ?? '-');
    appendRecapColor(lines, 'Motif 2', patternMotifColors?.motif2 ?? '-');
  }
  lines.push('');

  if (layers.length) {
    lines.push('--- ELEMENTS GRAPHIQUES ---');
    let logoIdx = 0;
    const handledPatternMotifIds = new Set<string>();

    for (const layer of layers) {
      const zone = ZONE_LABELS[layer.zone] ?? layer.zone ?? '-';
      const cx = Math.round((layer.uvCenter?.[0] ?? 0.5) * 100);
      const cy = Math.round((layer.uvCenter?.[1] ?? 0.5) * 100);
      const sx = Math.round((layer.uvSize?.[0] ?? 0) * 100);
      const sy = Math.round((layer.uvSize?.[1] ?? 0) * 100);
      const rot = radToDeg(layer.uvRotation ?? 0);

      lines.push('');

      if (layer.data?.isMotif) {
        const motifName = layer.data?.motifLabel || layer.data?.fileName || layer.data?.motifSourceKey || 'Inconnu';
        const isPatternMotif = Boolean(layer.data?.isPatternMotif);
        if (isPatternMotif) {
          const motifId = String(layer.data?.motifId || layer.id || motifName);
          if (handledPatternMotifIds.has(motifId)) continue;
          handledPatternMotifIds.add(motifId);
          const patternDesignKey = String(layer.data?.patternDesignKey || '').trim();
          const patternMaterialId = String(layer.data?.patternMaterialId || '').trim();
          let motifZone = zone;
          if (patternDesignKey) {
            motifZone = getPatternDesignLabel(patternDesignKey);
          } else if (patternMaterialId) {
            const targetMaterial = materials.find(m => m.id === patternMaterialId);
            motifZone = targetMaterial ? targetMaterial.element : patternMaterialId;
          }
          lines.push(`[MOTIF] ${motifName}`);
          lines.push(`  Zone       : ${motifZone}`);
          lines.push(`  Position   : X=${cx}%  Y=${cy}%`);
          lines.push(`  Taille     : ${sx}% x ${sy}%`);
          lines.push(`  Rotation   : ${rot} deg`);
          appendRecapColor(lines, 'Motif 1', patternMotifColors?.motif1 ?? '-');
          appendRecapColor(lines, 'Motif 2', patternMotifColors?.motif2 ?? '-');
          continue;
        }
        const motifDesignColors =
          (layer.data?.motifDesignColors as { design1?: string; design2?: string } | undefined) || {};
        lines.push(`[MOTIF] ${motifName}`);
        lines.push(`  Zone       : ${zone}`);
        lines.push(`  Position   : X=${cx}%  Y=${cy}%`);
        lines.push(`  Taille     : ${sx}% x ${sy}%`);
        lines.push(`  Rotation   : ${rot} deg`);
        appendRecapColor(lines, 'Design 1', motifDesignColors.design1 ?? '-');
        appendRecapColor(lines, 'Design 2', motifDesignColors.design2 ?? '-');
      } else if (layer.source?.kind === 'assetRef') {
        const tag = layer.source.collection === 'motif' ? 'MOTIF' : 'DESIGN';
        lines.push(`[${tag}] ${layer.source.value}`);
        lines.push(`  Zone       : ${zone}`);
        lines.push(`  Position   : X=${cx}%  Y=${cy}%`);
        lines.push(`  Taille     : ${sx}% x ${sy}%`);
        lines.push(`  Rotation   : ${rot} deg`);
      } else if (layer.type === 'image') {
        logoIdx += 1;
        const originalName = layer.data?.fileName ?? `logo_${logoIdx}`;
        lines.push(`[LOGO #${logoIdx}] ${originalName}`);
        lines.push(`  Fichier ZIP: logos/${logoIdx}_${originalName}`);
        lines.push(`  Zone       : ${zone}`);
        lines.push(`  Position   : X=${cx}%  Y=${cy}%`);
        lines.push(`  Taille     : ${sx}% x ${sy}%`);
        lines.push(`  Rotation   : ${rot} deg`);
      } else if (layer.type === 'text') {
        const d = layer.data ?? {};
        const textPreview = String(d.text ?? '').slice(0, 60);
        lines.push(`[TEXTE] "${textPreview}"`);
        lines.push(`  Zone       : ${zone}`);
        lines.push(`  Position   : X=${cx}%  Y=${cy}%`);
        lines.push(`  Taille     : ${sx}% x ${sy}%`);
        lines.push(`  Rotation   : ${rot} deg`);
        lines.push(`  Police     : ${d.fontFamily ?? '-'}  ${d.fontSize ?? '-'}px`);
        lines.push(`  Couleur    : ${d.color ?? '-'}`);
        if (d.strokeColor) {
          lines.push(`  Contour    : ${d.strokeColor}  epaisseur ${d.strokeWidth ?? 0}px`);
        }
      }
    }
  }

  lines.push('');
  lines.push('=== FIN DU RECAPITULATIF ===');
  return lines.join('\n');
}

export async function buildContactZip(params: {
  creation: any;
  captures: string[];
  materials: MaterialInfo[];
  modelName: string;
  resolveAssetRef?: (
    sourceValue: string,
    collection?: AssetRefCollection
  ) => Promise<ResolvedAssetRef | null> | ResolvedAssetRef | null;
}): Promise<{ zipBase64: string; recapText: string }> {
  const { creation, captures, materials, modelName, resolveAssetRef } = params;
  const zip = new JSZip();

  const captureFolder = zip.folder('apercus')!;
  for (let i = 0; i < captures.length; i += 1) {
    try {
      captureFolder.file(`${VIEW_LABELS[i] ?? `vue_${i + 1}`}.png`, dataUrlToBytes(captures[i]));
    } catch {
      // noop
    }
  }

  const logosFolder = zip.folder('logos')!;
  const layers: any[] = (creation.state?.layers ?? []).filter((l: any) => !l.data?.isSystem);
  let logoIdx = 0;
  for (const layer of layers) {
    if (layer.type === 'image' && !layer.data?.isMotif && layer.source?.kind === 'dataUrl') {
      logoIdx += 1;
      const ext = (layer.source.value as string).startsWith('data:image/png') ? 'png' : 'jpg';
      const originalName = layer.data?.fileName ?? `logo.${ext}`;
      const zipName = `${logoIdx}_${originalName}`;
      try {
        logosFolder.file(zipName, dataUrlToBytes(layer.source.value));
      } catch {
        // noop
      }
    }
  }

  const usedAssetRefs = collectUsedAssetRefs(layers);
  if (usedAssetRefs.length > 0) {
    const designFolder = zip.folder('designs_utilises');
    const motifFolder = zip.folder('motifs_utilises');
    const usedDesignNames = new Set<string>();
    const usedMotifNames = new Set<string>();
    const assetsManifestLines: string[] = ['=== ASSETS UTILISES ===', ''];

    for (const assetRef of usedAssetRefs) {
      const collection = assetRef.collection === 'motif' ? 'motif' : 'design';
      const folder = collection === 'motif' ? motifFolder : designFolder;
      const usedNames = collection === 'motif' ? usedMotifNames : usedDesignNames;
      const folderLabel = collection === 'motif' ? 'motifs_utilises' : 'designs_utilises';

      let resolved: ResolvedAssetRef | null = null;
      if (resolveAssetRef) {
        try {
          resolved = await resolveAssetRef(assetRef.value, assetRef.collection);
        } catch {
          resolved = null;
        }
      }

      const fallbackUrl = /^(https?:\/\/|\/|data:)/i.test(assetRef.value) ? assetRef.value : '';
      const candidateDataUrl = resolved?.dataUrl || '';
      const candidateUrl = resolved?.url || fallbackUrl;

      let bytes: Uint8Array | null = null;
      let ext = 'bin';
      let bytesOrigin: 'dataUrl' | 'url' | null = null;

      if (candidateDataUrl) {
        const parsed = parseDataUrlPayload(candidateDataUrl);
        if (parsed) {
          bytes = parsed.bytes;
          ext = inferExtensionFromMime(parsed.mime);
          bytesOrigin = 'dataUrl';
        }
      }

      if (!bytes && candidateUrl) {
        if (candidateUrl.startsWith('data:')) {
          const parsed = parseDataUrlPayload(candidateUrl);
          if (parsed) {
            bytes = parsed.bytes;
            ext = inferExtensionFromMime(parsed.mime);
            bytesOrigin = 'dataUrl';
          }
        } else {
          const fetched = await fetchAssetBytesFromUrl(candidateUrl);
          if (fetched) {
            bytes = fetched.bytes;
            ext = fetched.ext || ext;
            bytesOrigin = 'url';
          }
        }
      }

      if (!bytes || bytes.length === 0) {
        assetsManifestLines.push(`[MISSING] ${collection} | ${assetRef.value}`);
        continue;
      }

      const stemCandidates = [resolved?.name, assetRef.label, assetRef.value];
      const stemSource =
        stemCandidates.find(candidate => !!candidate && !looksLikeDataUrl(String(candidate))) ||
        `${collection}_inline`;
      const extFromSource = inferExtensionFromPath(resolved?.url || assetRef.value || '');
      const finalExt = bytesOrigin === 'dataUrl' ? ext || 'bin' : extFromSource || ext || 'bin';
      const finalName = ensureUniqueFileName(stemSource, finalExt, usedNames);
      folder?.file(finalName, bytes);
      assetsManifestLines.push(`[OK] ${collection} | ${assetRef.value} -> ${folderLabel}/${finalName}`);
    }

    assetsManifestLines.push('');
    assetsManifestLines.push('=== FIN ===');
    zip.file('assets_utilises.txt', assetsManifestLines.join('\n'));
  }

  const recapText = buildRecap(creation, materials, modelName);
  zip.file('recap.txt', recapText);

  const creationStripped = JSON.parse(JSON.stringify(creation));
  for (const layer of creationStripped.state?.layers ?? []) {
    if (layer.source?.kind === 'dataUrl') {
      layer.source.value = '[voir dossier /logos]';
    }
  }
  zip.file('creation.json', JSON.stringify(creationStripped, null, 2));

  const zipBase64 = await zip.generateAsync({
    type: 'base64',
    compression: 'DEFLATE',
    compressionOptions: { level: 6 },
  });

  return { zipBase64, recapText };
}
